import * as React from 'react'

interface StringListFieldRenderProps {
  value: unknown
  onChange: (value: string[]) => void
}

/** Edits a string array without changing the published General V1 data shape. */
export function StringListFieldRender({ value, onChange }: StringListFieldRenderProps) {
  const items = Array.isArray(value)
    ? value.map(item => (typeof item === 'string' ? item : String(item ?? '')))
    : []

  const updateItem = (index: number, nextValue: string) => {
    onChange(items.map((item, itemIndex) => (itemIndex === index ? nextValue : item)))
  }

  const moveItem = (index: number, offset: -1 | 1) => {
    const targetIndex = index + offset
    if (targetIndex < 0 || targetIndex >= items.length) return

    const nextItems = [...items]
    ;[nextItems[index], nextItems[targetIndex]] = [nextItems[targetIndex], nextItems[index]]
    onChange(nextItems)
  }

  const removeItem = (index: number) => {
    onChange(items.filter((_, itemIndex) => itemIndex !== index))
  }

  return (
    <div className="flex flex-col gap-2">
      {items.map((item, index) => (
        <div key={index} className="flex items-center gap-1.5">
          <input
            aria-label={`Item ${index + 1}`}
            type="text"
            value={item}
            onChange={event => updateItem(index, event.target.value)}
            className="min-w-0 flex-1 rounded-md border border-slate-300 px-2.5 py-2 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
          />
          <button
            type="button"
            aria-label={`Move item ${index + 1} up`}
            disabled={index === 0}
            onClick={() => moveItem(index, -1)}
            className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-35"
          >
            <span aria-hidden="true">^</span>
          </button>
          <button
            type="button"
            aria-label={`Move item ${index + 1} down`}
            disabled={index === items.length - 1}
            onClick={() => moveItem(index, 1)}
            className="rounded-md border border-slate-200 p-2 text-slate-600 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-35"
          >
            <span aria-hidden="true">v</span>
          </button>
          <button
            type="button"
            aria-label={`Remove item ${index + 1}`}
            onClick={() => removeItem(index)}
            className="rounded-md border border-red-200 p-2 text-red-600 hover:bg-red-50"
          >
            <span aria-hidden="true">x</span>
          </button>
        </div>
      ))}

      <button
        type="button"
        aria-label="Add item"
        onClick={() => onChange([...items, 'New item'])}
        className="inline-flex items-center justify-center gap-1.5 rounded-md border border-dashed border-slate-300 px-3 py-2 text-sm font-medium text-slate-700 hover:border-blue-400 hover:bg-blue-50 hover:text-blue-700"
      >
        <span aria-hidden="true">+</span>
        Add item
      </button>
    </div>
  )
}
